// components/admin/AdminTickets.jsx

import React, { useEffect, useRef, useState } from "react";
import {
    getAdminTickets,
    createTicket,
    updateTicket,
    toggleTicket,
    deleteTicket,
    createAnnonce,
    deleteAnnonce,
    updatePublicite,
} from "../services/AdminService";
import EventSearchSelect from "./Eventsearchselect";

const AdminTickets = () => {

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Form nouveau ticket
    const [newTitre, setNewTitre] = useState("");

    // Form annonce (par ticket ouvert)
    const [openAnnonceForm, setOpenAnnonceForm] = useState(null); // ticket_id ou null
    const [annonceType, setAnnonceType] = useState("event");
    const [annonceEventId, setAnnonceEventId] = useState("");
    const [annonceTitre, setAnnonceTitre] = useState("");
    const [annonceSelectedEvents, setAnnonceSelectedEvents] = useState([]); // events choisis (objets)
    const [annonceImageFile, setAnnonceImageFile] = useState(null); // File (publicite)

    // Édition d'une publicité existante
    const [editingPubId, setEditingPubId] = useState(null); // annonce.id en cours d'édition
    const [editPubTitre, setEditPubTitre] = useState("");
    const [editPubEvents, setEditPubEvents] = useState([]); // objets event
    const [editPubImageFile, setEditPubImageFile] = useState(null); // 🆕 nouvelle image (File) ou null

    const fileInputRef = useRef(null);
    const editFileInputRef = useRef(null); // 🆕 input file du mode édition

    // ── Charger les tickets ───────────────────────────────
    const loadTickets = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAdminTickets();
            setTickets(Array.isArray(data) ? data : data.tickets || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTickets();
    }, []);

    // ── Actions tickets ──────────────────────────────────
    const handleCreateTicket = async () => {
        if (!newTitre.trim()) return;
        try {
            await createTicket({ titre: newTitre.trim() });
            setNewTitre("");
            loadTickets();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleToggle = async (id) => {
        try {
            await toggleTicket(id);
            loadTickets();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDeleteTicket = async (id) => {
        if (!window.confirm("Supprimer ce ticket et ses annonces ?")) return;
        try {
            await deleteTicket(id);
            loadTickets();
        } catch (err) {
            alert(err.message);
        }
    };

    // 🔃 Changer la position d'un ticket (↑ / ↓)
    const moveTicket = async (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= tickets.length) return;

        // Réordonne localement
        const arr = [...tickets];
        [arr[index], arr[target]] = [arr[target], arr[index]];

        // Position = ordre dans la liste (0,1,2,...)
        const reordered = arr.map((t, i) => ({ ...t, position: i }));
        setTickets(reordered); // mise à jour optimiste (instantané)

        try {
            // Persiste uniquement les 2 tickets qui ont bougé
            await Promise.all([
                updateTicket(reordered[index].id, { position: index }),
                updateTicket(reordered[target].id, { position: target }),
            ]);
        } catch (err) {
            alert(err.message);
            loadTickets(); // resync si erreur
        }
    };

    const handleRename = async (ticket) => {
        const titre = window.prompt("Nouveau titre :", ticket.titre);
        if (titre === null || titre.trim() === "") return;
        try {
            await updateTicket(ticket.id, { titre: titre.trim() });
            loadTickets();
        } catch (err) {
            alert(err.message);
        }
    };

    // ── Actions annonces ─────────────────────────────────
    const resetAnnonceForm = () => {
        setOpenAnnonceForm(null);
        setAnnonceType("event");
        setAnnonceEventId("");
        setAnnonceTitre("");
        setAnnonceSelectedEvents([]);
        setAnnonceImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleCreateAnnonce = async (ticketId) => {
        // Validations côté front
        if (annonceType === "event" && !annonceEventId) {
            alert("Sélectionne un événement.");
            return;
        }
        if (annonceType === "publicite" && !annonceImageFile) {
            alert("Sélectionne une image (jpg, jpeg ou png) pour la publicité.");
            return;
        }
        if (annonceType === "publicite" && annonceSelectedEvents.length === 0) {
            alert("Ajoute au moins un événement à la publicité.");
            return;
        }

        try {
            if (annonceType === "event") {
                // JSON simple
                await createAnnonce({
                    ticket_id: ticketId,
                    type: "event",
                    event_id: Number(annonceEventId),
                });
            } else {
                // FormData avec fichier image
                const fd = new FormData();
                fd.append("ticket_id", ticketId);
                fd.append("type", "publicite");
                if (annonceTitre) fd.append("titre", annonceTitre);
                fd.append("image", annonceImageFile);

                annonceSelectedEvents.forEach((ev) =>
                    fd.append("events[]", ev.id)
                );

                await createAnnonce(fd);
            }

            resetAnnonceForm();
            loadTickets();
        } catch (err) {
            alert(err.message);
        }
    };

    // ── Édition d'une publicité ───────────────────────────
    const startEditPub = (annonce) => {
        setEditingPubId(annonce.id);
        setEditPubTitre(annonce.publicite?.titre || "");
        setEditPubEvents(annonce.publicite?.events || []);
        setEditPubImageFile(null); // 🆕 pas de nouvelle image au départ
        if (editFileInputRef.current) editFileInputRef.current.value = "";
    };

    const cancelEditPub = () => {
        setEditingPubId(null);
        setEditPubTitre("");
        setEditPubEvents([]);
        setEditPubImageFile(null); // 🆕
        if (editFileInputRef.current) editFileInputRef.current.value = "";
    };

    const saveEditPub = async (annonceId) => {
        if (editPubEvents.length === 0) {
            alert("La publicité doit contenir au moins un événement.");
            return;
        }
        try {
            await updatePublicite(annonceId, {
                titre: editPubTitre || null,
                events: editPubEvents.map((e) => e.id),
                image: editPubImageFile, // 🆕 null si l'image n'a pas changé
            });
            cancelEditPub();
            loadTickets();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDeleteAnnonce = async (id) => {
        if (!window.confirm("Supprimer cette annonce ?")) return;
        try {
            await deleteAnnonce(id);
            loadTickets();
        } catch (err) {
            alert(err.message);
        }
    };

    // ── Rendu ────────────────────────────────────────────
    if (loading) {
        return <div className="p-6 text-gray-500">Chargement des tickets…</div>;
    }

    if (error) {
        return (
            <div className="p-6">
                <p className="text-red-600 mb-3">⚠️ {error}</p>
                <button
                    onClick={loadTickets}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 max-w-4xl mx-auto">
            <h1
                data-testid="page-titre"
                className="text-2xl font-bold mb-6 text-gray-800"
            >
                Gestion des tickets & annonces
            </h1>

            {/* Création d'un ticket */}
            <div className="flex gap-2 mb-8">
                <input
                    type="text"
                    data-testid="input-nouveau-ticket"
                    value={newTitre}
                    onChange={(e) => setNewTitre(e.target.value)}
                    placeholder="Titre du nouveau ticket"
                    maxLength={50}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <button
                    data-testid="btn-ajouter-ticket"
                    title="Créer un nouveau ticket"
                    onClick={handleCreateTicket}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium"
                >
                    + Ajouter
                </button>
            </div>

            {/* Liste des tickets */}
            {tickets.length === 0 && (
                <p className="text-gray-500">Aucun ticket pour l'instant.</p>
            )}

            <div className="space-y-4">
                {tickets.map((ticket, index) => (
                    <div
                        key={ticket.id}
                        className={`border rounded-xl p-4 shadow-sm transition-colors ${ticket.isDisabled
                            ? "bg-red-50 border-red-200 border-l-4 border-l-red-400 opacity-90"
                            : "bg-white border-gray-200"
                            }`}
                    >
                        {/* En-tête ticket */}
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                                <span
                                    data-testid={`ticket-${ticket.id}-titre`}
                                    className="text-lg font-semibold text-gray-800"
                                >
                                    {ticket.titre}
                                </span>
                                <span
                                    data-testid={`ticket-${ticket.id}-meta`}
                                    className="text-xs text-gray-400"
                                >
                                    #{ticket.id} · pos {ticket.position ?? "—"}
                                </span>
                                {ticket.isDisabled && (
                                    <span
                                        data-testid={`ticket-${ticket.id}-badge-desactive`}
                                        className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full"
                                    >
                                        désactivé
                                    </span>
                                )}
                            </div>

                            <div className="flex items-center gap-2 text-sm">
                                {/* Flèches de position */}
                                <div className="flex flex-col leading-none mr-1">
                                    <button
                                        onClick={() => moveTicket(index, -1)}
                                        disabled={index === 0}
                                        title="Monter"
                                        className={`text-xs px-1 ${index === 0 ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:text-indigo-600"}`}
                                    >
                                        ▲
                                    </button>
                                    <button
                                        onClick={() => moveTicket(index, 1)}
                                        disabled={index === tickets.length - 1}
                                        title="Descendre"
                                        className={`text-xs px-1 ${index === tickets.length - 1 ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:text-indigo-600"}`}
                                    >
                                        ▼
                                    </button>
                                </div>

                                <button
                                    onClick={() => handleRename(ticket)}
                                    className="px-2 py-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                >
                                    Renommer
                                </button>
                                <button
                                    onClick={() => handleToggle(ticket.id)}
                                    className="px-2 py-1 text-amber-600 hover:bg-amber-50 rounded"
                                >
                                    {ticket.isDisabled ? "Réactiver" : "Désactiver"}
                                </button>
                                <button
                                    onClick={() => handleDeleteTicket(ticket.id)}
                                    className="px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>

                        {/* Annonces du ticket */}
                        <div className="mt-3 pl-2 border-l-2 border-gray-100 space-y-2">
                            {(ticket.annonces || []).length === 0 && (
                                <p className="text-xs text-gray-400">Aucune annonce.</p>
                            )}

                            {(ticket.annonces || []).map((annonce) => (
                                <div
                                    key={annonce.id}
                                    className="bg-gray-50 rounded-lg px-3 py-2"
                                >
                                    {/* Ligne principale de l'annonce */}
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 text-sm">
                                            {/* Miniature image (publicité) */}
                                            {annonce.type === "publicite" && annonce.image && (
                                                <img
                                                    src={annonce.image}
                                                    alt="pub"
                                                    className="w-8 h-8 rounded object-cover bg-gray-200"
                                                    onError={(e) => (e.target.style.display = "none")}
                                                />
                                            )}

                                            <span
                                                className={`text-xs px-2 py-0.5 rounded-full ${annonce.type === "event"
                                                    ? "bg-blue-100 text-blue-700"
                                                    : "bg-purple-100 text-purple-700"
                                                    }`}
                                            >
                                                {annonce.type}
                                            </span>

                                            <span className="text-gray-700">
                                                {annonce.type === "event"
                                                    ? annonce.event?.nomEven ||
                                                    `Event #${annonce.event_id}`
                                                    : annonce.publicite?.titre || "Publicité"}
                                            </span>
                                        </div>

                                        <div className="flex gap-2">
                                            {/* Modifier (publicité uniquement) */}
                                            {annonce.type === "publicite" &&
                                                editingPubId !== annonce.id && (
                                                    <button
                                                        data-testid={`annonce-${annonce.id}-modifier`}
                                                        title="Modifier cette publicité"
                                                        onClick={() => startEditPub(annonce)}
                                                        className="text-xs text-indigo-600 hover:underline"
                                                    >
                                                        modifier
                                                    </button>
                                                )}
                                            <button
                                                data-testid={`annonce-${annonce.id}-supprimer`}
                                                title="Supprimer cette annonce"
                                                onClick={() => handleDeleteAnnonce(annonce.id)}
                                                className="text-xs text-red-500 hover:underline"
                                            >
                                                supprimer
                                            </button>
                                        </div>
                                    </div>

                                    {/* Sous-events de la publicité */}
                                    {annonce.type === "publicite" && (
                                        <div className="mt-2 pl-2">
                                            {editingPubId === annonce.id ? (
                                                /* ── Mode édition ── */
                                                <div className="bg-white border border-indigo-100 rounded-lg p-2 space-y-2">
                                                    <input
                                                        type="text"
                                                        value={editPubTitre}
                                                        onChange={(e) => setEditPubTitre(e.target.value)}
                                                        placeholder="Titre de la publicité"
                                                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                    />

                                                    {/* 🆕 Image de la publicité (titre + image modifiables) */}
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-xs text-gray-500">
                                                            Image de la publicité
                                                        </label>
                                                        <div className="flex items-center gap-3">
                                                            {/* Aperçu : nouvelle image si choisie, sinon image actuelle */}
                                                            {editPubImageFile ? (
                                                                <img
                                                                    src={URL.createObjectURL(editPubImageFile)}
                                                                    alt="aperçu"
                                                                    className="w-16 h-16 rounded object-cover bg-gray-200"
                                                                />
                                                            ) : annonce.image ? (
                                                                <img
                                                                    src={annonce.image}
                                                                    alt="image actuelle"
                                                                    className="w-16 h-16 rounded object-cover bg-gray-200"
                                                                    onError={(e) => (e.target.style.display = "none")}
                                                                />
                                                            ) : (
                                                                <div className="w-16 h-16 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-[11px]">
                                                                    aucune
                                                                </div>
                                                            )}

                                                            <input
                                                                ref={editFileInputRef}
                                                                type="file"
                                                                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                                                onChange={(e) =>
                                                                    setEditPubImageFile(e.target.files[0] || null)
                                                                }
                                                                className="text-sm"
                                                            />
                                                        </div>
                                                        <span className="text-[11px] text-gray-400">
                                                            Laisse vide pour conserver l'image actuelle.
                                                        </span>
                                                    </div>

                                                    <EventSearchSelect
                                                        clearOnSelect
                                                        placeholder="Ajouter un événement…"
                                                        onSelect={(event) => {
                                                            if (!event) return;
                                                            setEditPubEvents((prev) =>
                                                                prev.some((e) => e.id === event.id)
                                                                    ? prev
                                                                    : [...prev, event]
                                                            );
                                                        }}
                                                    />

                                                    {/* Chips events */}
                                                    <div className="flex flex-wrap gap-2">
                                                        {editPubEvents.map((ev) => (
                                                            <span
                                                                key={ev.id}
                                                                className="flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full"
                                                            >
                                                                {ev.nomEven}
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setEditPubEvents((prev) =>
                                                                            prev.filter((e) => e.id !== ev.id)
                                                                        )
                                                                    }
                                                                    className="text-indigo-500 hover:text-red-600 font-bold"
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>

                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => saveEditPub(annonce.id)}
                                                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs"
                                                        >
                                                            Enregistrer
                                                        </button>
                                                        <button
                                                            onClick={cancelEditPub}
                                                            className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-xs"
                                                        >
                                                            Annuler
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                /* ── Affichage des events ── */
                                                <div className="flex flex-wrap gap-2">
                                                    {(annonce.publicite?.events || []).length === 0 ? (
                                                        <span className="text-xs text-gray-400">
                                                            Aucun événement lié.
                                                        </span>
                                                    ) : (
                                                        annonce.publicite.events.map((ev) => (
                                                            <span
                                                                key={ev.id}
                                                                className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full"
                                                            >
                                                                {ev.nomEven}
                                                            </span>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Bouton + formulaire d'ajout d'annonce */}
                            {openAnnonceForm === ticket.id ? (
                                <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
                                    {/* Type d'annonce */}
                                    <select
                                        value={annonceType}
                                        onChange={(e) => {
                                            setAnnonceType(e.target.value);
                                            setAnnonceEventId("");
                                            setAnnonceSelectedEvents([]);
                                            setAnnonceImageFile(null);
                                            if (fileInputRef.current) fileInputRef.current.value = "";
                                        }}
                                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                                    >
                                        <option value="event">event</option>
                                        <option value="publicite">publicite</option>
                                    </select>

                                    {annonceType === "event" ? (
                                        /* 🔍 Event : recherche par nom, AUCUN champ image */
                                        <EventSearchSelect
                                            onSelect={(event) =>
                                                setAnnonceEventId(event ? event.id : "")
                                            }
                                        />
                                    ) : (
                                        /* 📢 Publicité : titre + events + image fichier */
                                        <>
                                            <input
                                                type="text"
                                                value={annonceTitre}
                                                onChange={(e) => setAnnonceTitre(e.target.value)}
                                                placeholder="Titre de la publicité"
                                                maxLength={50}
                                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                            />
                                            {/* 🔍 Multi-sélection d'événements par recherche */}
                                            <div className="flex flex-col gap-1">
                                                <label className="text-xs text-gray-500">
                                                    Événements liés <span className="text-red-500">*</span>
                                                </label>

                                                <EventSearchSelect
                                                    clearOnSelect
                                                    placeholder="Rechercher un événement à ajouter…"
                                                    onSelect={(event) => {
                                                        if (!event) return;
                                                        setAnnonceSelectedEvents((prev) =>
                                                            prev.some((e) => e.id === event.id)
                                                                ? prev // déjà ajouté
                                                                : [...prev, event]
                                                        );
                                                    }}
                                                />

                                                {/* Liste des events choisis (chips) */}
                                                {annonceSelectedEvents.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-1">
                                                        {annonceSelectedEvents.map((ev) => (
                                                            <span
                                                                key={ev.id}
                                                                className="flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full"
                                                            >
                                                                {ev.nomEven}
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setAnnonceSelectedEvents((prev) =>
                                                                            prev.filter((e) => e.id !== ev.id)
                                                                        )
                                                                    }
                                                                    className="text-indigo-500 hover:text-red-600 font-bold"
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <label className="text-xs text-gray-500">
                                                    Image (jpg, jpeg, png) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                                    onChange={(e) =>
                                                        setAnnonceImageFile(e.target.files[0] || null)
                                                    }
                                                    className="text-sm"
                                                />
                                                {annonceImageFile && (
                                                    <img
                                                        src={URL.createObjectURL(annonceImageFile)}
                                                        alt="aperçu"
                                                        className="w-20 h-20 rounded object-cover mt-1"
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleCreateAnnonce(ticket.id)}
                                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                                        >
                                            Enregistrer
                                        </button>
                                        <button
                                            onClick={resetAnnonceForm}
                                            className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                                        >
                                            Annuler
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setOpenAnnonceForm(ticket.id)}
                                    className="text-xs text-indigo-600 hover:underline mt-1"
                                >
                                    + Ajouter une annonce
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminTickets;